import React from "react";
import { Box, Container, Typography, Chip } from "@mui/material";
import {
  Home,
  NavigateNext,
  LocalOffer,
  TrendingUp,
  Store,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface OffersHeroProps {
  selectedMall?: {
    id: string;
    name: string;
  } | null;
  stats: {
    total: number;
    expiringSoon: number;
    newOffers: number;
  };
}

/**
 * OffersHero Component
 *
 * Hero section for the Offers page with breadcrumb navigation,
 * title, subtitle, and statistics chips
 */
const OffersHero: React.FC<OffersHeroProps> = ({ selectedMall, stats }) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #1a237e 100%)", // Premium navy
        color: "white",
        pt: { xs: 3, md: 4 },
        pb: { xs: 4, md: 5 },
        position: "relative",
        overflow: "hidden",
        zIndex: 1000,
        boxShadow: "0 4px 20px rgba(26, 35, 126, 0.3)",
      }}
    >
      {/* Gold accent pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.08,
          backgroundImage:
            "radial-gradient(circle, rgba(212, 175, 55, 0.4) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Animated overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 80% 20%, rgba(212, 175, 55, 0.15) 0%, transparent 60%)",
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Breadcrumb */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 3,
            }}
          >
            <Home
              sx={{ fontSize: 20, cursor: "pointer" }}
              onClick={() => navigate("/")}
            />
            <NavigateNext sx={{ fontSize: 20 }} />
            {selectedMall && (
              <>
                <Typography
                  variant="body2"
                  onClick={() => navigate("/explore")}
                  sx={{
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Explore
                </Typography>
                <NavigateNext sx={{ fontSize: 20 }} />
                <Typography
                  variant="body2"
                  onClick={() => navigate(`/mall/${selectedMall.id}`)}
                  sx={{
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {selectedMall.name}
                </Typography>
                <NavigateNext sx={{ fontSize: 20 }} />
              </>
            )}
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Offers
            </Typography>
          </Box>

          {/* Title and Subtitle */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              Discover Exclusive Offers
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.95,
                fontWeight: 400,
                fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" },
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Find the best deals at your favorite malls
            </Typography>
          </Box>

          {/* Stats in Hero - Only show when mall is selected */}
          {selectedMall && (
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2, md: 3 },
                justifyContent: "center",
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Chip
                icon={<LocalOffer />}
                label={`${stats.total} Total`}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  px: { xs: 1, sm: 1.5 },
                  "& .MuiChip-icon": { color: "white" },
                }}
              />
              <Chip
                icon={<TrendingUp />}
                label={`${stats.expiringSoon} Expiring Soon`}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  px: { xs: 1, sm: 1.5 },
                  "& .MuiChip-icon": { color: "white" },
                }}
              />
              <Chip
                icon={<Store />}
                label={selectedMall.name}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  px: { xs: 1, sm: 1.5 },
                  "& .MuiChip-icon": { color: "white" },
                }}
              />
            </Box>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default OffersHero;
